import { metrics } from '@seek/metrics-js';

import { MOBILE_INTERCEPT } from '../../components/pages/LoginPage/hashRoutes';
import getHashRoute from '../../utils/getHashRoute';
import { handleDone } from '../../utils/mobile/forMobile';
import type { Analytics, TrackLinkFn } from '../analytics/types';

import { deployCookieMonster } from './../../utils/cookieMonster';
import { Flow, type LoginOptions } from './types';
import getWebAuth from './utils/getWebAuth';
import { mapError } from './utils/mapError';

export const getOnRedirectingFn =
  (
    trackLink: TrackLinkFn,
    { flow, isMobileClient }: { flow?: Flow; isMobileClient: boolean },
    attempt: number = 0,
  ) =>
  (done: () => void) => {
    const initTracking = () => {
      trackLink(
        flow === Flow.Register
          ? {
              eventName: 'registration_form_submit_succeeded',
              registrationFlow: 'new user',
              identityProvider: 'auth0',
            }
          : {
              eventName: 'login_form_submit_succeeded',
              identityProvider: 'auth0',
            },
      );

      // count and flush before redirection occurs
      metrics.count(
        flow === Flow.Register
          ? 'form_submit_ok_register'
          : 'form_submit_ok_login',
        [`attempt:${attempt}`],
      );
      metrics.flush();
    };

    // handle custom callback for mobile appm for flow === Flow.Register and for flow === Flow.Login
    if (isMobileClient) {
      handleDone().initDone(done, initTracking);
      const h = getHashRoute(MOBILE_INTERCEPT);

      window.location.hash = h;

      return;
    }

    initTracking();

    setTimeout(done, 1000);
  };

export const initiateSocialLogin = (socialProvider: string): void => {
  const webAuth = getWebAuth();

  webAuth.authorize({
    connection: socialProvider,
  });
};

export const login = async (
  { trackLink }: Analytics,
  {
    emailAddress,
    password,
    realm,
    flow = Flow.Login,
    isMobileClient = false,
    captchaToken,
  }: LoginOptions,
  attempt: number = 1,
): Promise<true> =>
  new Promise((resolve, reject) => {
    const webAuth = getWebAuth();
    webAuth.login(
      {
        username: emailAddress,
        email: emailAddress,
        password,
        realm,
        ...(captchaToken && {
          recaptcha_token: captchaToken,
        }),
        onRedirecting: getOnRedirectingFn(
          trackLink,
          {
            flow,
            isMobileClient,
          },
          attempt,
        ),
      },
      async (error) => {
        if (error) {
          if (error?.statusCode === 431 && attempt <= 3) {
            deployCookieMonster(attempt);
            await login(
              { trackLink, trackPageView: () => {} },
              { emailAddress, password, realm, flow, isMobileClient },
              attempt + 1,
            ).catch((expTwo) => {
              const reason = mapError(
                expTwo,
                trackLink,
                `login.error.try.${attempt}.after.431`,
              );
              reject(reason);
            });
          } else {
            const reason = mapError(error, trackLink, 'login.error');
            reject(reason);
            return;
          }
        }
        resolve(true);
      },
    );
  });
